import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import React from 'react';

export default function WhoItsFor() {
  // Obtain refs and visibility flags from the custom hook
  const { ref: headRef, isVisible: headVisible } = useScrollAnimation();
  const { ref: gridRef, isVisible: gridVisible } = useScrollAnimation({ threshold: 0.1 });

  const roles = [
    {
      icon: 'ri-briefcase-line',
      title: 'Project Managers',
      description:
        'Lead project teams with practical, culturally safe ways of working across the project lifecycle.',
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Designers and Planners',
      description: 'Apply Connecting with Country principles in planning and design decisions, not just documentation.',
    },
    {
      icon: 'ri-plant-line',
      title: 'Landscape Architects',
      description:
        'Design landscapes that reflect connection to Country and respond to cultural significance.',
    },
    {
      icon: 'ri-tools-line',
      title: 'Engineers and Delivery Teams',
      description: 'Bring Country-led thinking into construction and delivery, including site practices and project staging.',
    },
    {
      icon: 'ri-team-line',
      title: 'Engagement Consultants',
      description:
        'Strengthen respectful engagement practice and support project teams working with Traditional Custodians.',
    },
    {
      icon: 'ri-government-line',
      title: 'Government Project Leads',
      description: 'Move beyond policy compliance to genuinely embed Country-centred practice across NSW project delivery.',
    },
    {
      icon: 'ri-shopping-bag-line',
      title: 'Procurement and Commercial Teams',
      description: 'Support procurement, access setup and rollout across project teams and suppliers',
    },
  ];

  return (
    <section id="who-its-for" className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div
          ref={headRef} // ← removed TypeScript “as” assertion – not valid JSX syntax
          className={`text-center mb-16 transition-all duration-700 ease-out ${
            headVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <span className="text-xs uppercase tracking-widest text-[#BA3D03] font-semibold">
            YOUR TEAM
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0E0503] mt-4">
            Built for the people delivering NSW projects
          </h2>
        </div>

        {/* Roles grid — first 6 cards */}
        <div
          ref={gridRef}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {roles.slice(0, 6).map((role, index) => (
            <div
              key={index}
              className={`bg-white border border-[#E8C580] rounded-xl p-8 hover:-translate-y-1 transition-all duration-500 hover:shadow-lg cursor-pointer flex flex-col ${
                gridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="w-12 h-12 rounded-full bg-[#E48523] flex items-center justify-center text-white text-2xl mb-5 flex-shrink-0">
                <i className={role.icon}></i>
              </div>
              <h3 className="text-xl font-bold text-[#0E0503] mb-3">{role.title}</h3>
              <p className="text-[#3D1203] opacity-80 leading-relaxed">{role.description}</p>
            </div>
          ))}
        </div>

        {/* Divider — For Teams */}
        <div className="mt-10 mb-6 flex items-center gap-4">
          <div className="flex-1 h-px bg-[#E8C580]/40"></div>
          <span className="text-xs uppercase tracking-widest text-[#BA3D03] font-semibold px-3">
            For procurement and rollout
          </span>
          <div className="flex-1 h-px bg-[#E8C580]/40"></div>
        </div>

        {/* Last row — featured card full-width to match grid */}
        <div>
          <div
            className={`w-full bg-[#3D1203] rounded-xl p-8 transition-all duration-400 ease-out cursor-pointer border-2 border-transparent hover:border-[#E48523] hover:bg-[#4a1804] hover:shadow-2xl hover:shadow-[#E48523]/20 hover:-translate-y-1.5 hover:scale-[1.005] ${
              gridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ transitionDelay: '700ms' }}
          >
            {/* Featured badge */}
            <div className="flex items-center justify-between mb-5">
              {/* Amber icon with pulse glow */}
              <div className="relative flex-shrink-0">
                <span
                  className="absolute inset-0 rounded-full bg-[#E48523]/40 animate-ping"
                  style={{ animationDuration: '2.2s' }}
                ></span>
                <span
                  className="absolute inset-[-4px] rounded-full bg-[#E48523]/20 blur-sm"
                  style={{ animation: 'amberGlow 2.2s ease-in-out infinite' }}
                ></span>
                <div className="relative w-12 h-12 rounded-full bg-[#E48523] flex items-center justify-center text-white text-2xl">
                  <i className={roles[6].icon}></i>
                </div>
              </div>
              <span className="text-[10px] uppercase tracking-widest text-[#E48523] font-semibold border border-[#E48523]/40 rounded-full px-3 py-1">
                Company Access
              </span>
            </div>

            {/* Body — side-by-side on desktop, stacked on mobile */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div className="flex-1">
                <h3 className="text-xl font-bold text-white mb-3">{roles[6].title}</h3>
                <p className="text-white/80 leading-relaxed mb-3">{roles[6].description}</p>
                <p className="text-xs text-white/40 leading-relaxed">
                  We'll confirm pricing, access setup and next steps by email.
                </p>
              </div>
              <div className="flex-shrink-0 w-full sm:w-auto">
                <button
                  onClick={() => {
                    const el = document.getElementById('contact-cta');
                    if (el) {
                      el.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-5 py-2.5 bg-[#E48523] hover:bg-[#BA3D03] text-white rounded-lg text-xs font-semibold transition-all duration-200 shadow-md hover:shadow-lg whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-price-tag-3-line"></i>
                  Request Company Pricing
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Keyframe for amber glow pulse */}
        <style>{`
          @keyframes amberGlow {
            0%, 100% { opacity: 0.2; transform: scale(1); }
            50% { opacity: 0.55; transform: scale(1.18); }
          }
        `}</style>
      </div>
    </section>
  );
}
